import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, ElementRef, OnInit, Optional, Renderer2, inject } from '@angular/core';
import { ICategoryDataV3 } from '@df/catalog/category/category';
import { App } from '@df/core/app';
import { FacetUrlService } from '@df/module-facet-url/core/facet-url.service';
import { FacetOverridePipe } from '@df/module-facet-url/util/facet-override.pipe';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { BgColorOptions, ColorOptions, HorizontalAlignmentOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { HrefComponent } from '@df/ui/common/href/href.component';
import { SizeClassDirective } from '@df/ui/common/size-class.directive';
import { UiComponent } from '@df/ui/ui.component';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ReadMoreComponent } from '../../../../ui/common/read-more.component';
import { ShellComponent } from '../../../../ui/display/local-shell.component';
import { Subscription } from 'rxjs';
import { CategoryViewComponent } from '../../../category/category-view/category-view.component';
import { Category } from '../../../category/category.decorator';
interface IPlpHeaderLink {
    quick_link_copy: string;
    quick_link_url: string;
}
enum EPlpHeaderVariant {
    fullImage = 'full_image',
    imageAndText = 'image_and_text',
    textHeader = 'text_header'
}
interface IPageComponentPLPHeadersData extends NICmsContentComponent.IData {
    variant: EPlpHeaderVariant;
    image: string;
    title: string;
    subtitle: string;
    subtitleLimit?: number;
    image_url?: string;
    is_quicklinks?: boolean;
    quick_links: IPlpHeaderLink[];
    color: string;
    bg_color: string;
    caption_align?: string;
    image_align?: string;
    reverse?: boolean;
}
@Component({
    selector: 'plp-headers',
    templateUrl: 'plp-headers.component.html',
    styleUrls: ['plp-headers.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        CommonModule,
        SizeClassDirective,
        SizeClassDirective,
        HrefComponent,
        ReadMoreComponent,
        FacetOverridePipe,
        CarouselModule,
        ImageModule,
        ShellComponent,
        ICmsClientModule,
        ReadMoreComponent
    ]
})
@ICmsComponent<PLPHeadersComponent>({
    id: 'plp_headers',
    name: 'PLP Headers Layout',
    tags: ['PLP', 'Banner', 'Header'],
    fields: [
        <NICmsContentField.IChoiceField>{
            type: NICmsContentField.EFieldType.choice,
            key: 'variant',
            title: 'Variant',
            required: true,
            options: [
                UndefinedOption,
                { key: EPlpHeaderVariant.fullImage, title: 'Full Image' },
                { key: EPlpHeaderVariant.imageAndText, title: 'Image and text' },
                { key: EPlpHeaderVariant.textHeader, title: 'Text Header' }
            ]
        },
        <NICmsContentField.IField>{
            type: NICmsContentField.EFieldType.image,
            key: 'image',
            title: 'Image',
            required: true,
            canShow: data => data.variant !== EPlpHeaderVariant.textHeader
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'title',
            title: 'Title',
            required: true
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'subtitle',
            title: 'Subtitle',
            required: true
        },
        {
            key: 'subtitleLimit',
            type: NICmsContentField.EFieldType.number,
            title: 'Subtitle Limit',
            default: 200,
            description: 'Please remember that by default same limit will be set to all devices including mobiles, setting up a separate limit for mobile scope is recommended.'
        },
        {
            type: NICmsContentField.EFieldType.text,
            key: 'image_url',
            title: 'Image Url',
            canShow: data => data.variant !== EPlpHeaderVariant.textHeader
        },
        <NICmsContentField.IChoiceField>{
            key: 'caption_align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Horizontal text alignment',
            options: HorizontalAlignmentOptions,
            default: 'left'
        },
        <NICmsContentField.IChoiceField>{
            key: 'image_align',
            type: NICmsContentField.EFieldType.choice,
            title: 'Horizontal image alignment',
            options: HorizontalAlignmentOptions,
            default: 'left',
            canShow: data => data.variant === EPlpHeaderVariant.fullImage
        },
        <NICmsContentField.IToggleField>{
            key: 'reverse',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Revserse',
            labels: { true: 'Yes', false: 'No' },
            canShow: data => data.variant === EPlpHeaderVariant.imageAndText
        },
        <NICmsContentField.IToggleField>{
            key: 'is_quicklinks',
            type: NICmsContentField.EFieldType.toggle,
            title: 'Quicklinks Visible',
            default: true
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'quick_links',
            title: 'Quick Links',
            itemName: 'Quick Link',
            fields: [
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'quick_link_copy',
                    title: 'Quick Link Copy',
                    required: true
                },
                <NICmsContentField.IField>{
                    type: NICmsContentField.EFieldType.text,
                    key: 'quick_link_url',
                    title: 'Quick Link URL',
                    required: true
                }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'color',
            type: NICmsContentField.EFieldType.choice,
            title: 'Font Colour',
            colorPreview: true,
            options: ColorOptions,
            default: 'col-w'
        },
        <NICmsContentField.IChoiceField>{
            key: 'bg_color',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background Colour',
            colorPreview: true,
            options: BgColorOptions,
            default: 'bg-col-5'
        }
    ]
})
export class PLPHeadersComponent extends UiComponent implements NICmsContentComponent.IComponent, OnInit {
    @ICmsContent({
        type: 'component'
    })
    component?: IPageComponentPLPHeadersData;

    readMoreOpen = false;
    variantTypes = EPlpHeaderVariant;

    app = inject(App);

    facetUrlService = inject(FacetUrlService);
    listingSubscription?: Subscription = new Subscription();
    quickLinks?: IPlpHeaderLink[];
    facetPlpBanner?: string;
    constructor(
        protected elementRef: ElementRef,
        protected renderer2: Renderer2,
        @Optional() protected categoryView?: CategoryViewComponent
    ) {
        super();
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.subscriptions.add(this.app.meta.subscribe(() => this.onListingChange()));
        this.subscribeToListing();
        this.subscriptions.add(
            this.categoryView?.categoryViewModelChangeSubject.subscribe(() => {
                this.subscribeToListing();
                this.onListingChange();
            })
        );
        this.onListingChange();
    }
    subscribeToListing(): void {
        this.listingSubscription?.unsubscribe();
        this.listingSubscription = this.categoryView?.listing?.subscribe(() => this.onListingChange());
    }

    onListingChange(): void {
        const facetData = this.category && this.facetUrlService.retrieveFacetData(this.category?.id);
        this.quickLinks =
            facetData?.['quick_links']?.map(quickLink => {
                return {
                    quick_link_copy: quickLink.label,
                    quick_link_url: quickLink.link
                };
            }) || this.component?.quick_links;
        this.facetPlpBanner = facetData?.['plp_banner'];
        this.detectChanges();
    }
    get plpBanner(): string | undefined {
        return this.facetPlpBanner || this.component?.image;
    }
    get category(): Category<ICategoryDataV3> | undefined {
        return this.categoryView?.model;
    }

    get variant(): EPlpHeaderVariant | undefined {
        return this.component?.variant;
    }
    get quicklinks(): boolean {
        return !!this.component?.is_quicklinks;
    }
    onReadMoreVisibilityUpdate(event: boolean): void {
        this.readMoreOpen = event;
        this.detectChanges();
    }
    get isFullImage(): boolean {
        return this.variant === EPlpHeaderVariant.fullImage;
    }
    get isImageAndText(): boolean {
        return this.variant === EPlpHeaderVariant.imageAndText;
    }
    get isReversed(): boolean {
        return !!this.component?.reverse;
    }
    override ngOnDestroy(): void {
        this.listingSubscription?.unsubscribe();
        super.ngOnDestroy();
    }
}
